/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { memo } from 'react';
import * as React from 'react';
import { Button, FormControl, FormHelperText, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { PeriodQueryParams } from '../../../data/api/apiCommon.typings';
import { RadioGroupFilter, RadioGroupFilterProps } from '../../Filters/RadioGroupFilter/RadioGroupFilter';
import RangeDatePicker from '../../RangeDatePicker/RangeDatePicker';
import moment, { Moment } from 'moment';

const MAX_DATE_RANGE_VALUE = 1095; // 3 years

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        title: {
            fontSize: 16,
            fontWeight: 500,
            marginBottom: theme.spacing(1),
        },
        select: {
            fontSize: 14,
            fontWeight: 400,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.green.light,
            marginBottom: theme.spacing(2),
            height: 38,
        },
        quickSelectWrapper: {
            marginTop: theme.spacing(-1),
            display: 'flex',
            flexDirection: 'column',
        },
        quickSelectCaption: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'self-end',
            justifyContent: 'space-between',
        },
        quickSelectInputs: {
            display: 'flex',
            flexDirection: 'row',
        },
        unitsSelect: {
            marginLeft: theme.spacing(1),
            height: 40,
        },
        updateButton: {
            marginBottom: theme.spacing(1),
            '&:disabled': {
                color: theme.palette.disabledText.main,
            },
        },
        errorText: {
            marginTop: '3px',
            marginLeft: 0,
            marginRight: 0,
            lineHeight: 1.3,
        },
    }),
);

export enum TimeRangeFilterType {
    QUICK_SELECT = 'QUICK_SELECT',
    COMMONLY_USED = 'COMMONLY_USED',
    SHOW_DATES = 'SHOW_DATES',
}

export enum QuickSelectUnits {
    Minutes = 'minutes',
    Hours = 'hours',
    Days = 'days',
    Weeks = 'weeks',
    Months = 'months',
    Years = 'years',
}

export enum CommonlyUsedOptions {
    Today = 'today',
    Yesterday = 'yesterday',
    ThisWeek = 'thisWeek',
    ThisMonth = 'thisMonth',
    ThisYear = 'thisYear',
    WeekToDate = 'weekToDate',
    MonthToDate = 'monthToDate',
    YearToDate = 'yearToDate',
}

export interface QuickSelectParams {
    value: number;
    units: QuickSelectUnits;
}

export interface TimeRangeFilterParams {
    shownFilter: TimeRangeFilterType;
    showDates: PeriodQueryParams;
    quickSelect: QuickSelectParams;
    commonlyUsed: CommonlyUsedOptions;
}

export const getTimeRangePeriodByQuickSelect = (value: number, units: QuickSelectUnits): PeriodQueryParams => {
    let dateFrom: Moment = null;
    const dateTo: Moment = moment();

    switch (units) {
        case QuickSelectUnits.Minutes:
            dateFrom = moment().subtract(value, 'minutes');
            break;
        case QuickSelectUnits.Hours:
            dateFrom = moment().subtract(value, 'hours');
            break;
        case QuickSelectUnits.Days:
            dateFrom = moment().subtract(value, 'days');
            break;
        case QuickSelectUnits.Weeks:
            dateFrom = moment().subtract(value, 'weeks');
            break;
        case QuickSelectUnits.Months:
            dateFrom = moment().subtract(value, 'months');
            break;
        case QuickSelectUnits.Years:
            dateFrom = moment().subtract(value, 'years');
            break;
    }

    return { dateFrom: dateFrom.utc().format(), dateTo: dateTo.utc().format() };
};

interface TimeRangeFilterProps {
    filterParams: TimeRangeFilterParams;
    onChangeFilterParams(params: TimeRangeFilterParams): void;
    onChangeTimeRangePeriod(dateFrom: string, dateTo: string): void;
    hideUpdateInQuickSelect?: boolean;
}

const TimeRangeFilter = (props: TimeRangeFilterProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filterParams, hideUpdateInQuickSelect, onChangeFilterParams, onChangeTimeRangePeriod } = props;

    const [lastValue, setLastValue] = React.useState(filterParams.quickSelect.value.toString());
    const [errorMessage, setErrorMessage] = React.useState(null);

    const validateLastValueInput = () => {
        const lastNumber = Number(lastValue);
        if (lastNumber < 1 || lastNumber > 100) {
            setErrorMessage('RegistryScreens.errors.enterIntegerValue');
        } else if (
            (lastNumber > 3 && filterParams.quickSelect.units === QuickSelectUnits.Years) ||
            (lastNumber > 36 && filterParams.quickSelect.units === QuickSelectUnits.Months)
        ) {
            setErrorMessage('RegistryScreens.errors.maxDatesRange');
        } else {
            setErrorMessage(null);
            if (filterParams.quickSelect.value !== lastNumber)
                onChangeFilterParams({
                    ...filterParams,
                    quickSelect: {
                        ...filterParams.quickSelect,
                        value: lastNumber,
                    },
                });
            const { dateFrom, dateTo } = getTimeRangePeriodByQuickSelect(lastNumber, filterParams.quickSelect.units);
            onChangeTimeRangePeriod(dateFrom, dateTo);
        }
    };

    const validateUnitsInput = (units: QuickSelectUnits) => {
        const lastNumber = Number(lastValue);
        if (
            (lastNumber > 3 && units === QuickSelectUnits.Years) ||
            (lastNumber > 36 && units === QuickSelectUnits.Months)
        ) {
            setErrorMessage('RegistryScreens.errors.maxDatesRange');
            onChangeFilterParams({
                ...filterParams,
                quickSelect: {
                    value: 1,
                    units,
                },
            });
        } else {
            setErrorMessage(null);
            onChangeFilterParams({
                ...filterParams,
                quickSelect: {
                    value: lastNumber,
                    units,
                },
            });
            const { dateFrom, dateTo } = getTimeRangePeriodByQuickSelect(lastNumber, units);
            onChangeTimeRangePeriod(dateFrom, dateTo);
        }
    };

    const getTimeRangePeriodByCommonlyUsedOption = (option: CommonlyUsedOptions): PeriodQueryParams => {
        let dateFrom: Moment = null;
        let dateTo: Moment = null;

        switch (option) {
            case CommonlyUsedOptions.Today:
                dateFrom = moment().startOf('day');
                dateTo = moment().endOf('day');
                break;
            case CommonlyUsedOptions.Yesterday:
                dateFrom = moment().startOf('day').subtract(1, 'day');
                dateTo = moment().endOf('day').subtract(1, 'day');
                break;
            case CommonlyUsedOptions.ThisWeek:
                dateFrom = moment().startOf('isoWeek');
                dateTo = moment().endOf('isoWeek');
                break;
            case CommonlyUsedOptions.ThisMonth:
                dateFrom = moment().startOf('month');
                dateTo = moment().endOf('month');
                break;
            case CommonlyUsedOptions.ThisYear:
                dateFrom = moment().startOf('year');
                dateTo = moment().endOf('year');
                break;
            case CommonlyUsedOptions.WeekToDate:
                dateFrom = moment().startOf('day').subtract(1, 'week');
                dateTo = moment().endOf('day');
                break;
            case CommonlyUsedOptions.MonthToDate:
                dateFrom = moment().startOf('day').subtract(1, 'month');
                dateTo = moment().endOf('day');
                break;
            case CommonlyUsedOptions.YearToDate:
                dateFrom = moment().startOf('day').subtract(1, 'year');
                dateTo = moment().endOf('day');
                break;
        }

        return { dateFrom: dateFrom.utc().format(), dateTo: dateTo.utc().format() };
    };

    const handleTimeRangeSubfilterSelect = (event: any) => {
        if (event.target.value === TimeRangeFilterType.QUICK_SELECT && errorMessage !== null) {
            setErrorMessage(null);
            setLastValue(filterParams.quickSelect.value.toString());
        }
        onChangeFilterParams({
            ...filterParams,
            shownFilter: event.target.value,
        });
        let period: PeriodQueryParams = null;
        switch (event.target.value) {
            case TimeRangeFilterType.QUICK_SELECT:
                period = getTimeRangePeriodByQuickSelect(
                    filterParams.quickSelect.value,
                    filterParams.quickSelect.units,
                );
                break;
            case TimeRangeFilterType.COMMONLY_USED:
                period = getTimeRangePeriodByCommonlyUsedOption(filterParams.commonlyUsed);
                break;
            case TimeRangeFilterType.SHOW_DATES:
                period = { dateFrom: filterParams.showDates.dateFrom, dateTo: filterParams.showDates.dateTo };
                break;
        }
        onChangeTimeRangePeriod(period.dateFrom, period.dateTo);
    };

    const commonlyUsedOptionsProps: RadioGroupFilterProps<CommonlyUsedOptions> = {
        options: Object.values(CommonlyUsedOptions),
        selected: filterParams.commonlyUsed,
        onChange(value: CommonlyUsedOptions): void {
            onChangeFilterParams({ ...filterParams, commonlyUsed: value });
            const { dateFrom, dateTo } = getTimeRangePeriodByCommonlyUsedOption(value);
            onChangeTimeRangePeriod(dateFrom, dateTo);
        },
        getItemContent(value: CommonlyUsedOptions): React.ReactElement {
            return t(`app.periodsCommonlyUsed.${value}`);
        },
    };

    return (
        <div data-testid="timeRangeFilter">
            <FormControl fullWidth variant="outlined">
                <Typography className={classes.title}>{t('RegistryScreens.timeRange') + ':'}</Typography>

                <Select
                    data-testid="filterTypeBtn"
                    className={classes.select}
                    value={filterParams.shownFilter}
                    onChange={handleTimeRangeSubfilterSelect}
                >
                    {Object.values(TimeRangeFilterType).map((item, index) => {
                        return (
                            <MenuItem value={item} key={index}>
                                {t(`RegistryScreens.timeRangeTypes.${item}`)}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            {filterParams.shownFilter === TimeRangeFilterType.QUICK_SELECT && (
                <div className={classes.quickSelectWrapper}>
                    <div className={classes.quickSelectCaption}>
                        <Typography>{t('app.last')}</Typography>
                        {!hideUpdateInQuickSelect && (
                            <Button
                                className={classes.updateButton}
                                color="primary"
                                size="medium"
                                onClick={() => {
                                    const { dateFrom, dateTo } = getTimeRangePeriodByQuickSelect(
                                        filterParams.quickSelect.value,
                                        filterParams.quickSelect.units,
                                    );
                                    onChangeTimeRangePeriod(dateFrom, dateTo);
                                }}
                                disabled={errorMessage !== null}
                            >
                                {t('domain.update')}
                            </Button>
                        )}
                    </div>

                    <FormControl fullWidth>
                        <div data-testid="quickSelectInputs" className={classes.quickSelectInputs}>
                            <TextField
                                data-testid="value"
                                placeholder={'[1-100]'}
                                variant="outlined"
                                size="small"
                                type="number"
                                value={lastValue}
                                onChange={(event) => {
                                    setLastValue(event.target.value.replace(/[^0-9]/, ''));
                                }}
                                onBlur={() => validateLastValueInput()}
                                onKeyPress={(event) => {
                                    if (/[^0-9.]/.test(event.key)) {
                                        event.preventDefault();
                                    }

                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        validateLastValueInput();
                                    }
                                }}
                                error={errorMessage !== null}
                            />
                            <Select
                                data-testid="units"
                                fullWidth
                                variant="outlined"
                                className={classes.unitsSelect}
                                value={filterParams.quickSelect.units}
                                onChange={(event: any) => validateUnitsInput(event.target.value)}
                            >
                                {Object.values(QuickSelectUnits).map((item, index) => {
                                    return (
                                        <MenuItem value={item} key={index}>
                                            {t(`app.timeUnits.${item}`)}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </div>
                        <FormHelperText error={errorMessage !== null} className={classes.errorText}>
                            {t(errorMessage)}
                        </FormHelperText>
                    </FormControl>
                </div>
            )}
            {filterParams.shownFilter === TimeRangeFilterType.COMMONLY_USED && (
                <RadioGroupFilter {...commonlyUsedOptionsProps} />
            )}
            {filterParams.shownFilter === TimeRangeFilterType.SHOW_DATES && (
                <RangeDatePicker
                    onDateChange={({ dateFrom, dateTo }: { dateFrom?: moment.Moment; dateTo?: moment.Moment }) => {
                        onChangeFilterParams({
                            ...filterParams,
                            showDates: {
                                dateFrom: dateFrom.utc().format(),
                                dateTo: dateTo.utc().format(),
                            },
                        });
                        onChangeTimeRangePeriod(dateFrom.utc().format(), dateTo.utc().format());
                    }}
                    dateFrom={filterParams.showDates.dateFrom}
                    dateTo={filterParams.showDates.dateTo}
                    isShowTime
                    timeInterval={5}
                    minDateSubstrAmount={MAX_DATE_RANGE_VALUE}
                    isMinDate
                    isCustomLabel
                    isIcon
                    wrap
                    popperPlacement={'bottom-end'}
                />
            )}
        </div>
    );
};

export default memo(TimeRangeFilter);
